import { useState, useEffect, useRef } from 'react';
import api, { formatError } from '@/lib/api';
import { toast } from 'sonner';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Badge } from '@/components/ui/badge';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Textarea } from '@/components/ui/textarea';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '@/components/ui/dialog';
import { ShieldCheck, Upload, CheckCircle, XCircle, Clock, FileText, User, Loader2, Eye } from 'lucide-react';

export default function KycPage() {
  const [records, setRecords] = useState([]);
  const [stats, setStats] = useState(null);
  const [branches, setBranches] = useState([]);
  const [filterStatus, setFilterStatus] = useState('all');
  const [filterBranch, setFilterBranch] = useState('all');
  const [loading, setLoading] = useState(true);
  const [viewKyc, setViewKyc] = useState(null);
  const [reviewDialog, setReviewDialog] = useState(null);
  const [reviewNotes, setReviewNotes] = useState('');
  const [processing, setProcessing] = useState(false);

  const fetchData = async () => {
    try {
      const params = {};
      if (filterStatus !== 'all') params.status = filterStatus;
      if (filterBranch !== 'all') params.branch_id = filterBranch;
      const [rRes, sRes, bRes] = await Promise.all([
        api.get('/kyc/all', { params }),
        api.get('/kyc/stats'),
        api.get('/branches').catch(() => ({ data: [] }))
      ]);
      setRecords(rRes.data);
      setStats(sRes.data);
      setBranches(bRes.data);
    } catch (err) { toast.error(formatError(err.response?.data?.detail)); }
    finally { setLoading(false); }
  };
  useEffect(() => { fetchData(); }, [filterStatus, filterBranch]);

  const handleReview = async (action) => {
    if (!reviewDialog) return;
    setProcessing(true);
    try {
      await api.put(`/kyc/review/${reviewDialog.client_id}`, { action, notes: reviewNotes });
      toast.success(`KYC ${action === 'approve' ? 'approved' : 'rejected'}`);
      setReviewDialog(null);
      setReviewNotes('');
      fetchData();
    } catch (err) { toast.error(formatError(err.response?.data?.detail)); }
    finally { setProcessing(false); }
  };

  const statusIcon = (s) => s === 'verified' ? <CheckCircle size={14} className="text-brand-success" /> : s === 'rejected' ? <XCircle size={14} className="text-brand-error" /> : <Clock size={14} className="text-amber-500" />;

  return (
    <div className="space-y-6" data-testid="kyc-page">
      <div className="flex flex-col sm:flex-row justify-between gap-4">
        <div>
          <h1 className="font-heading text-2xl sm:text-3xl font-bold text-brand-text-primary tracking-tight">KYC Management</h1>
          <p className="text-sm text-brand-text-secondary mt-1">Review and manage client KYC submissions</p>
        </div>
      </div>

      {/* Stats */}
      {stats && (
        <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
          {[
            { l: 'Total', v: stats.total, c: 'text-brand-text-primary' },
            { l: 'Pending', v: stats.pending, c: 'text-amber-600' },
            { l: 'Verified', v: stats.verified, c: 'text-brand-success' },
            { l: 'Rejected', v: stats.rejected, c: 'text-brand-error' },
          ].map(s => (
            <Card key={s.l} className="border border-brand-border bg-white">
              <CardContent className="p-4"><p className="text-[10px] uppercase tracking-widest font-semibold text-brand-text-secondary">{s.l}</p><p className={`font-heading text-2xl font-bold ${s.c}`}>{s.v}</p></CardContent>
            </Card>
          ))}
        </div>
      )}

      {/* Filters */}
      <div className="flex gap-3 flex-wrap">
        <Select value={filterStatus} onValueChange={setFilterStatus}>
          <SelectTrigger className="w-40 bg-white border-brand-border rounded-lg text-sm" data-testid="kyc-filter-status"><SelectValue /></SelectTrigger>
          <SelectContent>
            <SelectItem value="all">All Status</SelectItem>
            <SelectItem value="pending">Pending</SelectItem>
            <SelectItem value="verified">Verified</SelectItem>
            <SelectItem value="rejected">Rejected</SelectItem>
          </SelectContent>
        </Select>
        <Select value={filterBranch} onValueChange={setFilterBranch}>
          <SelectTrigger className="w-44 bg-white border-brand-border rounded-lg text-sm" data-testid="kyc-filter-branch"><SelectValue /></SelectTrigger>
          <SelectContent>
            <SelectItem value="all">All Branches</SelectItem>
            {branches.map(b => <SelectItem key={b.id} value={b.id}>{b.name}</SelectItem>)}
          </SelectContent>
        </Select>
      </div>

      {/* Table */}
      <Card className="border border-brand-border bg-white">
        <CardContent className="p-0">
          <div className="overflow-x-auto">
            <table className="w-full text-sm" data-testid="kyc-table">
              <thead><tr className="border-b border-brand-border bg-brand-bg/50">
                {['Client', 'Name', 'PAN', 'Aadhaar', 'City', 'Docs', 'Status', 'Actions'].map(h => (
                  <th key={h} className="text-left py-3 px-4 text-xs uppercase tracking-wider font-semibold text-brand-text-secondary">{h}</th>
                ))}
              </tr></thead>
              <tbody>
                {loading ? <tr><td colSpan={8} className="py-12 text-center text-brand-text-secondary">Loading...</td></tr> :
                 records.length === 0 ? <tr><td colSpan={8} className="py-12 text-center text-brand-text-secondary">No KYC records found</td></tr> :
                 records.map(r => (
                  <tr key={r.id} className="border-b border-brand-border/50 hover:bg-brand-surface-hover transition-colors">
                    <td className="py-3 px-4 font-mono text-xs text-brand-primary font-semibold">{r.client_code || '-'}</td>
                    <td className="py-3 px-4 font-medium text-brand-text-primary">{r.full_name}</td>
                    <td className="py-3 px-4 text-xs font-mono">{r.pan_number || '-'}</td>
                    <td className="py-3 px-4 text-xs font-mono">{r.aadhaar_number ? `****${r.aadhaar_number.slice(-4)}` : '-'}</td>
                    <td className="py-3 px-4 text-brand-text-secondary">{r.city || '-'}</td>
                    <td className="py-3 px-4">
                      <div className="flex gap-1">
                        {r.pan_card_doc && <Badge className="text-[10px] bg-brand-bg text-brand-text-secondary border-0">PAN</Badge>}
                        {r.aadhaar_doc && <Badge className="text-[10px] bg-brand-bg text-brand-text-secondary border-0">Aadhaar</Badge>}
                        {r.photo_doc && <Badge className="text-[10px] bg-brand-bg text-brand-text-secondary border-0">Photo</Badge>}
                      </div>
                    </td>
                    <td className="py-3 px-4"><Badge className={`text-xs border-0 flex items-center gap-1 w-fit ${r.status === 'verified' ? 'bg-brand-success/15 text-brand-success' : r.status === 'rejected' ? 'bg-red-100 text-brand-error' : 'bg-amber-100 text-amber-700'}`}>{statusIcon(r.status)} {r.status}</Badge></td>
                    <td className="py-3 px-4">
                      <div className="flex gap-1">
                        <Button variant="ghost" size="icon" onClick={() => setViewKyc(r)} className="h-8 w-8 text-brand-primary" data-testid={`view-kyc-${r.client_id}`}><Eye size={14} /></Button>
                        {r.status === 'pending' && (
                          <Button variant="ghost" size="sm" onClick={() => { setReviewDialog(r); setReviewNotes(''); }} className="h-8 text-xs text-brand-primary" data-testid={`review-kyc-${r.client_id}`}>Review</Button>
                        )}
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </CardContent>
      </Card>

      {/* View KYC Details */}
      <Dialog open={!!viewKyc} onOpenChange={() => setViewKyc(null)}>
        <DialogContent className="sm:max-w-2xl max-h-[80vh] overflow-y-auto" data-testid="kyc-detail-dialog">
          <DialogHeader><DialogTitle className="font-heading text-xl flex items-center gap-2"><ShieldCheck size={20} className="text-brand-primary" /> KYC Details — {viewKyc?.full_name}</DialogTitle></DialogHeader>
          {viewKyc && (
            <div className="space-y-4 py-2">
              <div className="grid grid-cols-2 gap-3 text-sm">
                {[
                  ['Full Name', viewKyc.full_name], ['Date of Birth', viewKyc.date_of_birth], ['Father Name', viewKyc.father_name], ['Mother Name', viewKyc.mother_name],
                  ['Phone', viewKyc.phone], ['Email', viewKyc.email], ['PAN Number', viewKyc.pan_number], ['Aadhaar', viewKyc.aadhaar_number],
                  ['Address', viewKyc.address], ['City', viewKyc.city], ['State', viewKyc.state], ['PIN Code', viewKyc.pin_code],
                  ['Nominee Name', viewKyc.nominee_name], ['Nominee DOB', viewKyc.nominee_dob], ['Relationship', viewKyc.nominee_relationship],
                  ['Income Source', viewKyc.income_source], ['Occupation', viewKyc.occupation],
                ].map(([l, v]) => v ? <div key={l}><p className="text-[10px] uppercase text-brand-text-secondary font-semibold">{l}</p><p className="text-brand-text-primary">{v}</p></div> : null)}
              </div>
              {/* Documents */}
              <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
                {[['PAN Card', viewKyc.pan_card_doc], ['Aadhaar', viewKyc.aadhaar_doc], ['Photo', viewKyc.photo_doc], ['Signature', viewKyc.signature_doc]].map(([label, src]) => (
                  <div key={label} className="text-center">
                    <p className="text-[10px] uppercase text-brand-text-secondary font-semibold mb-1">{label}</p>
                    {src ? <img src={src} alt={label} className="w-full h-24 object-contain border border-brand-border rounded" /> : <div className="w-full h-24 bg-brand-bg rounded flex items-center justify-center text-xs text-brand-text-secondary">Not uploaded</div>}
                  </div>
                ))}
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>

      {/* Review Dialog */}
      <Dialog open={!!reviewDialog} onOpenChange={() => setReviewDialog(null)}>
        <DialogContent className="sm:max-w-md" data-testid="kyc-review-dialog">
          <DialogHeader><DialogTitle>Review KYC — {reviewDialog?.full_name}</DialogTitle></DialogHeader>
          <div className="space-y-3 py-2">
            <Label>Notes</Label>
            <Textarea value={reviewNotes} onChange={e => setReviewNotes(e.target.value)} placeholder="Review notes..." rows={3} className="bg-white border-brand-border rounded-lg" />
          </div>
          <DialogFooter className="gap-2">
            <Button onClick={() => handleReview('reject')} disabled={processing} variant="outline" className="rounded-lg text-brand-error border-brand-error/30" data-testid="kyc-reject-btn">
              <XCircle size={15} className="mr-1" /> Reject
            </Button>
            <Button onClick={() => handleReview('approve')} disabled={processing} className="bg-brand-success text-white rounded-lg" data-testid="kyc-approve-btn">
              {processing ? <Loader2 size={15} className="mr-1 animate-spin" /> : <CheckCircle size={15} className="mr-1" />} Approve
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
